import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  ConfirmationDialog,
  ErrorState,
  FailureNotice,
  InlineConfirmation,
  LoadingState,
  Notice,
  Section,
  Select,
  Tag,
  TextInput,
  cn,
  useLanguage,
  usePageTitle,
  useToast,
  useTranslate,
  CARD_SURFACE,
} from '@athena/ui'
import {
  adminCreateUserInput,
  adminDeleteUserInput,
  adminUpdateUserInput,
  adminSetUserAccessInput,
} from '@athena/validation'
import { formatNumber, type TranslationKey } from '@athena/i18n'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  AdminAccountDocument,
  ManagedUserDocument,
  ManagedUsersDocument,
  CreateManagedUserDocument,
  DeleteManagedUserDocument,
  UpdateManagedUserDocument,
  SetManagedUserAccessDocument,
  UserDeletionImpactDocument,
  type AdminManagedRole,
  type ManagedUserFieldsFragment,
  type UserDeletionImpactQuery,
} from '../../graphql/generated/operations.js'
import { AccountStatusTag, InvitationTag } from '../display/Tags.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'
import { fieldErrors, useFocusFirstInvalid } from '../../lib/validation.js'
import { ReasonField, UnsavedChanges } from './ManagementSupport.js'

/**
 * An account's own details: names, language and profiles.
 *
 * Routine edits only. Disabling the account used to be a second button beside
 * Save, sharing its reason field — so the most consequential thing on the
 * form looked like another way of finishing it. It is its own section now
 * (`UserAccess`), with its own reason, and deleting the account is further
 * down still, in the danger zone.
 */
export function UserManagement({ userId }: { userId: string }) {
  const t = useTranslate()
  const [saved, setSaved] = useState(false)
  const { data, loading, error } = useQuery(ManagedUserDocument, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  })
  if (!data && loading) return <LoadingState>{t('common.loading')}</LoadingState>
  if (error) return <ErrorState>{t('admin.loadError')}</ErrorState>
  if (!data?.adminManagedUser) return null
  const user = data.adminManagedUser
  return (
    <>
      <UserForm
        key={`${user.id}:${user.version}`}
        user={user}
        onSaved={() => setSaved(true)}
        // Here rather than in the form: the form remounts on every save (its
        // key is the record's version), and would take its own confirmation
        // with it.
        confirmation={
          saved ? <InlineConfirmation>{t('management.saved')}</InlineConfirmation> : null
        }
      />
      {user.studentRelationships?.length ? (
        <Section
          id="student-tutors"
          title={t('management.relationships')}
          className="border-t border-border pt-6"
        >
          <ul className={cn(CARD_SURFACE, 'divide-y divide-border')}>
            {user.studentRelationships.map((link) => (
              <li
                key={link.id}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-body-sm"
              >
                <span className="font-medium text-text">{link.displayName}</span>
                {link.suspended ? (
                  <Tag tone="muted" icon="⊘">
                    {t('admin.suspended')}
                  </Tag>
                ) : (
                  <Tag tone="positive" icon="●">
                    {t('admin.enabled')}
                  </Tag>
                )}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </>
  )
}

export function NewUserScreen() {
  const t = useTranslate()
  usePageTitle(t('management.createUser'))
  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      <UserForm />
    </div>
  )
}

function UserForm({
  user,
  onSaved,
  confirmation,
}: {
  user?: ManagedUserFieldsFragment
  onSaved?: () => void
  confirmation?: ReactNode
}) {
  const t = useTranslate()
  const userError = useFirstUserErrorText()
  const navigate = useNavigate()
  const { language } = useParams({ from: '/$language' })
  const initial = {
    email: user?.email ?? '',
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    preferredLanguage: user?.preferredLanguage ?? 'ES',
    roles: user?.roles ?? ['TUTOR'],
    reason: '',
  }
  const [values, setValues] = useState(initial)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  /**
   * A save that never got an answer. No retry of its own: sending it again is
   * pressing the same button again, with the reason and every value still
   * filled in.
   */
  const [failure, setFailure] = useState<unknown>()
  const [success, setSuccess] = useState(false)
  const [destination, setDestination] = useState<string | null>(null)
  const toast = useToast()
  const lock = useRef(false)
  const [busy, setBusy] = useState(false)
  const invalid = useFocusFirstInvalid<HTMLFormElement>()
  const refresh = {
    refetchQueries: [ManagedUsersDocument, AdminAccountDocument],
    awaitRefetchQueries: true,
  }
  const [create] = useMutation(CreateManagedUserDocument, refresh)
  const [update] = useMutation(UpdateManagedUserDocument, refresh)
  const dirty = !success && JSON.stringify(values) !== JSON.stringify(initial)
  // One level under the form's heading: h2 on the new-user page, where the
  // form is the page, and h3 on an account's page, where it is a section.
  const Subheading = user ? 'h3' : 'h2'
  // A created account opens on its own page, and is confirmed there: this
  // form, and anything it could say, is gone by then.
  useEffect(() => {
    if (!destination) return
    void navigate({
      to: '/$language/accounts/$userId',
      params: { language, userId: destination },
    }).then(() => toast.show(t('management.userCreated')))
  }, [destination, navigate, language, toast, t])
  const set = (key: keyof typeof values, value: string) => {
    setValues((v) => ({ ...v, [key]: value }))
    setSuccess(false)
  }
  const input = () =>
    user
      ? {
          userId: user.id,
          version: user.version,
          firstName: values.firstName,
          lastName: values.lastName,
          preferredLanguage: values.preferredLanguage,
          roles: values.roles,
          reason: values.reason,
        }
      : {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          preferredLanguage: values.preferredLanguage,
          roles: values.roles,
          reason: values.reason,
        }
  function validate() {
    const result = (user ? adminUpdateUserInput : adminCreateUserInput).safeParse(input())
    setErrors(result.success ? {} : fieldErrors(result.error.issues, t))
    if (!result.success) invalid.request()
    return result.success
  }
  /** Save the form. Resolves true only when the server made the change. */
  async function submit(): Promise<boolean> {
    if (lock.current || !validate()) return false
    lock.current = true
    setBusy(true)
    setError(null)
    setFailure(undefined)
    try {
      let result
      if (user) {
        const parsed = adminUpdateUserInput.parse(input())
        result = (await update({ variables: { input: parsed } })).data?.adminUpdateUser
      } else {
        const parsed = adminCreateUserInput.parse(input())
        result = (await create({ variables: { input: parsed } })).data?.adminCreateUser
      }
      if (!result?.user) {
        setError(userError(result?.userErrors, t('management.failed')))
        return false
      }
      setSuccess(true)
      onSaved?.()
      if (!user) setDestination(result.user.id)
      return true
    } catch (caught) {
      setFailure(caught)
      return false
    } finally {
      lock.current = false
      setBusy(false)
    }
  }
  return (
    <Section
      id="user-management"
      // The page's own heading when creating; a section of the account's
      // page, under its h1, when editing.
      level={user ? 2 : 1}
      title={t(user ? 'management.editUser' : 'management.createUser')}
      {...(user ? { className: 'border-t border-border pt-6' } : {})}
    >
      <UnsavedChanges dirty={dirty && !destination} />
      {!user && <Notice title={t('management.createHelp')} />}
      {confirmation}
      {/* A notice in error tone is an alert already; no second one around it. */}
      {error ? <Notice tone="error" title={error} /> : null}
      {failure === undefined ? null : (
        <FailureNotice title={t('management.notSaved')} error={failure} />
      )}
      <form
        ref={invalid.ref}
        // Athena says what is missing, in its own words and language; the
        // browser's tooltip would say it a second time, differently.
        noValidate
        className="flex w-full flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault()
          // A routine save asks nothing: the reason is the deliberate step,
          // and the audit log keeps it.
          void submit()
        }}
      >
        <fieldset disabled={busy || success} className="flex min-w-0 flex-col gap-5">
          <div className="grid min-w-0 max-w-3xl gap-4 sm:grid-cols-2">
            <Subheading className="text-h4 font-semibold text-text sm:col-span-2">
              {t('management.personalDetails')}
            </Subheading>
            <TextInput
              label={t('management.firstName')}
              value={values.firstName}
              onChange={(event) => set('firstName', event.target.value)}
              required
              maxLength={60}
              error={errors['firstName'] ?? ''}
            />
            <TextInput
              label={t('management.lastName')}
              value={values.lastName}
              onChange={(event) => set('lastName', event.target.value)}
              required
              maxLength={60}
              error={errors['lastName'] ?? ''}
            />
          </div>

          <div className="grid min-w-0 max-w-3xl gap-4 border-t border-border pt-5 sm:grid-cols-2">
            <Subheading className="text-h4 font-semibold text-text sm:col-span-2">
              {t('management.accountSettings')}
            </Subheading>
            <div className="sm:col-span-2">
              <TextInput
                label={t('management.email')}
                value={values.email}
                onChange={(event) => set('email', event.target.value)}
                type="email"
                required
                readOnly={!!user}
                error={errors['email'] ?? ''}
                helperText={user ? t('management.emailReadOnly') : ''}
              />
            </div>
            <Select
              label={t('management.language')}
              value={values.preferredLanguage}
              onValueChange={(value) => set('preferredLanguage', value)}
              options={[
                { value: 'ES', label: t('language.es') },
                { value: 'EN', label: t('language.en') },
              ]}
            />
            {!user ? (
              <Select
                label={t('management.roles')}
                value={values.roles[0] ?? 'TUTOR'}
                onValueChange={(role) =>
                  setValues((value) => ({
                    ...value,
                    roles: [role as 'TUTOR' | 'STUDENT'],
                  }))
                }
                options={[
                  { value: 'TUTOR', label: t('admin.roleTutor') },
                  { value: 'STUDENT', label: t('management.roleStudentInvited') },
                ]}
              />
            ) : (
              <Select
                label={t('management.roles')}
                helperText={t('management.profilesHelper')}
                value={profileSelection(values.roles)}
                onValueChange={(selection) => {
                  setSuccess(false)
                  setValues((value) => ({
                    ...value,
                    roles: rolesForProfileSelection(
                      selection as EditableProfileSelection,
                      value.roles,
                    ),
                  }))
                }}
                options={[
                  { value: 'NONE', label: t('management.profilesNone') },
                  { value: 'TUTOR', label: t('admin.roleTutor') },
                  ...(user.studentId
                    ? [
                        { value: 'STUDENT', label: t('admin.roleStudent') },
                        {
                          value: 'TUTOR_STUDENT',
                          label: t('management.profilesTutorStudent'),
                        },
                      ]
                    : []),
                ]}
              />
            )}
          </div>

          <div className="border-t border-border pt-5">
            <ReasonField
              value={values.reason}
              onChange={(value) => set('reason', value)}
              error={errors['reason'] ?? ''}
            />
          </div>
        </fieldset>
        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={busy || success} loading={busy}>
            {t(user ? 'management.save' : 'management.createUser')}
          </Button>
        </div>
      </form>
    </Section>
  )
}

/**
 * Whether the account can sign in, and the one control that changes it.
 *
 * Significant rather than destructive: disabling signs the person out
 * everywhere and keeps everything they own, and reactivating gives it all
 * back. So it has its own section, its own reason, and a `caution` dialog —
 * not a red one, and not a button beside Save.
 */
export function UserAccess({ userId }: { userId: string }) {
  const t = useTranslate()
  const userError = useFirstUserErrorText()
  const { data } = useQuery(ManagedUserDocument, {
    variables: { userId },
    fetchPolicy: 'cache-first',
  })
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [outcome, setOutcome] = useState<
    { saved: true } | { refusal: string } | { error: unknown } | undefined
  >()
  const [busy, setBusy] = useState(false)
  const lock = useRef(false)
  const invalid = useFocusFirstInvalid<HTMLDivElement>()
  const [access] = useMutation(SetManagedUserAccessDocument, {
    refetchQueries: [ManagedUsersDocument, AdminAccountDocument],
    awaitRefetchQueries: true,
  })
  const user = data?.adminManagedUser
  if (!user) return null

  function validate(): boolean {
    const result = adminSetUserAccessInput.safeParse({
      userId: user!.id,
      version: user!.version,
      reason,
      disabled: !user!.disabled,
    })
    const message = result.success ? '' : (fieldErrors(result.error.issues, t)['reason'] ?? '')
    setReasonError(message)
    if (!result.success) invalid.request()
    return result.success
  }

  /** Resolves true only when the server changed the account's access. */
  async function change(): Promise<boolean> {
    if (lock.current || !user) return false
    lock.current = true
    setBusy(true)
    setOutcome(undefined)
    try {
      const result = (
        await access({
          variables: {
            input: { userId: user.id, version: user.version, reason, disabled: !user.disabled },
          },
        })
      ).data?.adminSetUserAccess
      if (!result?.user) {
        setOutcome({ refusal: userError(result?.userErrors, t('management.failed')) })
        return false
      }
      setReason('')
      setOutcome({ saved: true })
      return true
    } catch (caught) {
      setOutcome({ error: caught })
      return false
    } finally {
      lock.current = false
      setBusy(false)
    }
  }

  const failure =
    outcome && 'refusal' in outcome ? (
      <Notice tone="error" title={outcome.refusal} />
    ) : outcome && 'error' in outcome ? (
      // Pressing the button again is the retry.
      <FailureNotice title={t('management.notSaved')} error={outcome.error} />
    ) : null

  return (
    <Section id="access" title={t('admin.sectionAccess')} className="border-t border-border pt-6">
      <div className="flex flex-wrap items-center gap-2">
        <AccountStatusTag disabled={user.disabled} />
        {user.pendingInvitation ? <InvitationTag /> : null}
        <span className="text-body-sm text-text-secondary">
          {t(user.emailVerified ? 'admin.emailVerified' : 'admin.emailUnverified')}
        </span>
      </div>
      <p className="max-w-prose text-body-sm text-text-secondary">
        {t(user.disabled ? 'admin.accessDisabledBody' : 'admin.accessActiveBody')}
      </p>
      {outcome && 'saved' in outcome ? (
        <InlineConfirmation>{t('management.saved')}</InlineConfirmation>
      ) : null}
      {/* A disable's failure is said inside its dialog; a reactivation's, here. */}
      {confirming ? null : failure}
      <div ref={invalid.ref} className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <ReasonField
          value={reason}
          onChange={(value) => {
            setReason(value)
            setReasonError('')
          }}
          error={reasonError}
        />
        <Button
          type="button"
          variant="secondary"
          className="shrink-0 sm:mt-[1.625rem]"
          loading={busy && !confirming}
          disabled={busy}
          onClick={() => {
            if (!validate()) return
            // Reactivating gives access back, so it asks nothing. Disabling
            // takes it away, so it is asked first.
            if (user.disabled) void change()
            else {
              setOutcome(undefined)
              setConfirming(true)
            }
          }}
        >
          {t(user.disabled ? 'management.reactivate' : 'management.disable')}
        </Button>
      </div>
      <ConfirmationDialog
        open={confirming}
        onOpenChange={setConfirming}
        // Recoverable — reactivating gives everything back — so `caution`,
        // not red, and nothing on it reads as a deletion.
        intent="caution"
        title={t('management.disableTitle', [user.email])}
        description={t('management.disableBody', [user.email])}
        confirmLabel={t('management.disable')}
        confirmingLabel={t('common.saving')}
        cancelLabel={t('management.keepActive')}
        failure={failure}
        onConfirm={change}
      />
    </Section>
  )
}

type EditableProfileSelection = 'NONE' | 'TUTOR' | 'STUDENT' | 'TUTOR_STUDENT'

function profileSelection(roles: readonly AdminManagedRole[]): EditableProfileSelection {
  const tutor = roles.includes('TUTOR')
  const student = roles.includes('STUDENT')
  if (tutor && student) return 'TUTOR_STUDENT'
  if (tutor) return 'TUTOR'
  if (student) return 'STUDENT'
  return 'NONE'
}

function rolesForProfileSelection(
  selection: EditableProfileSelection,
  currentRoles: readonly AdminManagedRole[],
): AdminManagedRole[] {
  const productRoles: AdminManagedRole[] = [
    ...(selection === 'TUTOR' || selection === 'TUTOR_STUDENT' ? (['TUTOR'] as const) : []),
    ...(selection === 'STUDENT' || selection === 'TUTOR_STUDENT' ? (['STUDENT'] as const) : []),
  ]
  return [...productRoles, ...currentRoles.filter((role) => role === 'ADMIN')]
}

type DeletionImpact = UserDeletionImpactQuery['adminUserDeletionImpact']

/** Kinds of record a deletion touches, in the order an administrator reads them. */
const DELETED_KINDS = [
  'practices',
  'practiceVersions',
  'questions',
  'assignments',
  'attempts',
  'tutorStudentLinks',
  'connectionRequests',
  'invitations',
  'provisionalStudents',
  'subscriptions',
  'usagePeriods',
  'aiUsageEntries',
  'upgradeRequests',
  'sessions',
  'providerIdentities',
  'signInCodes',
  'featureFlags',
  'auditEntries',
  'rateLimitCounters',
] as const satisfies readonly (keyof DeletionImpact['deleted'])[]
const DETACHED_KINDS = [
  'upgradeDecisions',
  'downgradeSelections',
] as const satisfies readonly (keyof DeletionImpact['detached'])[]
const PRESERVED_KINDS = [
  'connectedStudents',
  'connectedTutors',
  'sharedProvisionalStudents',
] as const satisfies readonly (keyof DeletionImpact['preserved'])[]

/**
 * What the deletion will do, as the server counted it: what goes, what stays
 * with this account taken out of it, and who is connected but unaffected.
 * Kinds with nothing to count are left out, so a bare account's list is short.
 */
function DeletionImpactSummary({ impact }: { impact: DeletionImpact }) {
  const t = useTranslate()
  const language = useLanguage()
  const group = <K extends string>(
    heading: TranslationKey,
    kinds: readonly K[],
    counts: Record<K, number>,
  ) => {
    const rows = kinds.filter((kind) => counts[kind] > 0)
    if (rows.length === 0) return null
    return (
      <div>
        <p className="text-body-sm font-semibold text-text">{t(heading)}</p>
        <dl className="mt-1 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-body-sm">
          {rows.map((kind) => (
            <div key={kind} className="contents">
              <dt className="min-w-0 break-words text-text-secondary">
                {t(`management.deletionImpact.kinds.${kind}` as TranslationKey)}
              </dt>
              <dd className="text-right tabular-nums text-text">
                {formatNumber(counts[kind], language)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-4">
      {impact.blocker ? (
        <Notice tone="error" title={t(`userError.${impact.blocker}` as TranslationKey)} />
      ) : null}
      {impact.otherStudentAttempts > 0 ? (
        <Notice tone="warning" title={t('management.deletionImpact.otherStudentsTitle')}>
          {t('management.deletionImpact.otherStudentsBody', [
            formatNumber(impact.otherStudentAttempts, language),
            formatNumber(impact.otherStudentsAffected, language),
          ])}
        </Notice>
      ) : null}
      {group('management.deletionImpact.deleted', DELETED_KINDS, impact.deleted)}
      {group('management.deletionImpact.detached', DETACHED_KINDS, impact.detached)}
      {group('management.deletionImpact.preserved', PRESERVED_KINDS, impact.preserved)}
    </div>
  )
}

/**
 * Permanently deleting the account: last on the page, apart from everything
 * else, and asked with the strongest confirmation the administration has — a
 * reason, the consequences counted by the server, and the address typed back.
 *
 * The counts are a preview, never an instruction: the server recounts inside
 * the deletion, and refuses it when more of other students' history would go
 * than the number confirmed here.
 *
 * A heading and one bordered row, not a red block: the button is the one red
 * thing, which is enough to say what it does without making the whole end of
 * the page read as an alarm.
 */
export function UserDangerZone({ userId }: { userId: string }) {
  const t = useTranslate()
  const client = useApolloClient()
  const userError = useFirstUserErrorText()
  const navigate = useNavigate()
  const { language } = useParams({ from: '/$language' })
  const {
    data,
    loading,
    error: queryError,
  } = useQuery(ManagedUserDocument, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  })
  const [reason, setReason] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)
  /**
   * A delete that never got an answer, said inside the dialog with the reason
   * and the typed email kept. The dialog stays open, but the notice asks for
   * the account to be reloaded first: the delete may have happened.
   */
  const [failure, setFailure] = useState<unknown>()
  const [open, setOpen] = useState(false)
  const toast = useToast()
  const [remove, removing] = useMutation(DeleteManagedUserDocument, {
    refetchQueries: [ManagedUsersDocument],
  })
  // Counted when the dialog opens and every time it does: a count from an
  // earlier opening may no longer be true.
  const preview = useQuery(UserDeletionImpactDocument, {
    variables: { userId },
    skip: !open,
    fetchPolicy: 'network-only',
  })
  const impact = open ? preview.data?.adminUserDeletionImpact : undefined

  if (!data && loading) return <LoadingState>{t('common.loading')}</LoadingState>
  if (queryError) return <ErrorState>{t('admin.loadError')}</ErrorState>
  if (!data?.adminManagedUser) return null
  const user = data.adminManagedUser
  // Wrong the moment it stops being the start of the address, not while it
  // is still being typed.
  const typedWrong = confirmation !== '' && !user.email.startsWith(confirmation)

  /** Resolves true only once the account is gone and the list is on screen. */
  async function deleteUser(): Promise<boolean> {
    if (!impact || impact.blocker) return false
    const parsed = adminDeleteUserInput.safeParse({
      userId: user.id,
      version: user.version,
      reason,
      // The number this administrator was shown, not one the server is asked to trust.
      confirmedOtherStudentAttempts: impact.otherStudentAttempts,
    })
    if (!parsed.success) {
      setError(fieldErrors(parsed.error.issues, t)['reason'] ?? t('management.errors.reason'))
      return false
    }
    setError(null)
    setFailure(undefined)
    let payload
    try {
      payload = (await remove({ variables: { input: parsed.data } })).data?.adminDeleteUser
    } catch (caught) {
      // The reason and the typed email stay in the dialog's fields.
      setFailure(caught)
      return false
    }
    if (!payload?.deleted) {
      setError(userError(payload?.userErrors, t('management.failed')))
      // Shown the new numbers, so confirming again confirms what is true now.
      if (payload?.userErrors.some(({ code }) => code === 'DELETION_IMPACT_CHANGED'))
        await preview.refetch()
      return false
    }
    // The mutation resolves only after the deletion's transaction committed,
    // so this is the first moment it is true to say the account is gone.
    await navigate({ to: '/$language', params: { language } })
    toast.show(t('management.deletedUser'))
    // Evicted only once the account screen is gone: while it is mounted, its
    // live queries would refetch the account that was just deleted. Lists
    // still holding a reference drop it once the entity is gone.
    for (const __typename of ['ManagedUser', 'AdminUser']) {
      const id = client.cache.identify({ __typename, id: user.id })
      if (id) client.cache.evict({ id })
    }
    client.cache.gc()
    return true
  }

  return (
    <Section
      id="danger-zone"
      title={t('management.dangerZone')}
      className="mt-4 border-t border-border pt-6"
    >
      <div
        className={cn(
          'flex flex-col gap-4 rounded-md border border-error-border bg-surface p-4',
          'sm:flex-row sm:items-center sm:justify-between',
        )}
      >
        <div className="min-w-0">
          <p className="text-body-sm font-semibold text-text">{t('management.deleteAccount')}</p>
          <p className="mt-1 max-w-prose break-words text-body-sm text-text-secondary">
            {t('management.deleteUserEffect', [user.email])}
          </p>
        </div>
        <Button
          type="button"
          variant="destructive"
          className="w-full shrink-0 sm:w-auto"
          loading={removing.loading}
          onClick={() => {
            setConfirmation('')
            setError(null)
            setFailure(undefined)
            setOpen(true)
          }}
        >
          {t('management.deleteAccount')}
        </Button>
      </div>
      <ConfirmationDialog
        open={open}
        onOpenChange={setOpen}
        // Permanent: the one confirmation in the administration that is red
        // and asks for the address to be typed, and it stays that way.
        intent="destructive"
        title={t('management.confirmDeleteTitle', [user.email])}
        description={t('management.confirmDeleteBody', [user.email])}
        confirmLabel={t('management.deleteAccountPermanently')}
        confirmingLabel={t('management.deleting')}
        // Nothing goes ahead until the impact is counted and allowed, and both
        // the reason and the exact address are in.
        confirmDisabled={
          !impact ||
          !!impact.blocker ||
          preview.loading ||
          confirmation !== user.email ||
          reason.trim() === ''
        }
        cancelLabel={t('management.keepAccount')}
        failure={
          error ? (
            <Notice tone="error" title={error} />
          ) : failure === undefined ? null : (
            <FailureNotice title={t('management.deleteUnconfirmed')} error={failure} />
          )
        }
        onConfirm={deleteUser}
      >
        <div className="flex flex-col gap-4">
          {preview.error ? (
            <Notice tone="error" title={t('management.deletionImpact.loadError')} />
          ) : impact ? (
            <DeletionImpactSummary impact={impact} />
          ) : (
            <p className="text-body-sm text-text-secondary">
              {t('management.deletionImpact.loading')}
            </p>
          )}
          <ReasonField value={reason} onChange={setReason} />
          <TextInput
            label={t('management.confirmDeleteInput')}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
            // Said as soon as it is wrong, so the disabled confirm button is
            // never the only sign of why nothing can be pressed.
            error={typedWrong ? t('management.errors.confirmEmail', [user.email]) : ''}
          />
        </div>
      </ConfirmationDialog>
    </Section>
  )
}
